import React, { useState, useEffect } from 'react';
import AppModal from '../common/Modal';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import SponsorLogoUpload from '../sponsors/SponsorLogoUpload';

function UpdateLivestreamModal({ show, onHide, initialData, onUpdated }) {
  const [description, setDescription] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [price, setPrice] = useState('');
  const [logoType, setLogoType] = useState('');
  const [sponsors, setSponsors] = useState([]);
  const [tempSponsorName, setTempSponsorName] = useState('');
  const [error, setError] = useState(null);

  // Fill state when initialData changes
  useEffect(() => {
    if (initialData) {
      setDescription(initialData.description || '');
      setStartTime(initialData.startTime || '');
      setEndTime(initialData.endTime || '');
      setPrice(initialData.price || '');
      setLogoType(initialData.logoType || '');
      setSponsors(initialData.sponsors || []);
    }
  }, [initialData]);

  const handleAddSponsor = () => {
    if (!tempSponsorName.trim()) {
      toast.warning('Please enter a sponsor name');
      return;
    }
    setSponsors(prev => [...prev, { sponsorName: tempSponsorName.trim(), logoUrl: '' }]);
    setTempSponsorName('');
  };

  const handleRemoveSponsor = (index) => {
    setSponsors(prev => prev.filter((_, i) => i !== index));
  };

  const handleLogoUpload = (index, logoUrl) => {
    setSponsors(prev => prev.map((sponsor, i) =>
      i === index ? { ...sponsor, logoUrl } : sponsor
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const updatedLivestream = { id: initialData.id, description, startTime, endTime, price, logoType, sponsors };

    try {
      const response = await api.put(`/livestream/${initialData.id}`, updatedLivestream);
      onUpdated(response.data);
      toast.success('Livestream updated successfully');
      onHide();
    } catch (err) {
      console.error('[UpdateLivestreamModal] Update failed:', err);
      setError('Update failed. Please try again.');
    }
  };

  const inputClass = "w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white";
  const labelClass = "block text-sm font-medium text-text-primary mb-1";

  return (
    <AppModal
      show={show}
      onHide={onHide}
      title="Update Livestream"
      size="lg"
    >
      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {typeof error === 'string' ? error : error?.message || 'An error occurred'}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Description */}
        <div>
          <label className={labelClass}>Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            className={inputClass}
          />
        </div>

        {/* Start & End Time */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Start Time</label>
            <input
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>End Time</label>
            <input
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              required
              className={inputClass}
            />
          </div>
        </div>

        {/* Price & Logo Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Price</label>
            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Logo Type</label>
            <input
              type="text"
              value={logoType}
              onChange={(e) => setLogoType(e.target.value)}
              required
              className={inputClass}
            />
          </div>
        </div>

        {/* Sponsors Section */}
        <div>
          <label className={labelClass}>Sponsors</label>

          {/* Add Sponsor row */}
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              placeholder="Sponsor Name"
              value={tempSponsorName}
              onChange={(e) => setTempSponsorName(e.target.value)}
              className={`${inputClass} flex-1`}
            />
            <button
              type="button"
              onClick={handleAddSponsor}
              className="px-4 py-2 bg-accent-grey text-white text-sm font-medium rounded-lg hover:bg-gray-600 transition-colors duration-200 whitespace-nowrap cursor-pointer border-none"
            >
              Add Sponsor
            </button>
          </div>

          {/* Sponsor list */}
          <div className="space-y-2">
            {sponsors.map((sponsor, index) => (
              <div
                key={index}
                className="flex items-center justify-between px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
              >
                <div className="flex items-center gap-4">
                  {sponsor.logoUrl && (
                    <img
                      src={sponsor.logoUrl}
                      alt={sponsor.sponsorName}
                      className="w-10 h-10 rounded object-contain bg-white"
                    />
                  )}
                  <div>
                    <strong className="text-sm text-text-primary">{sponsor.sponsorName}</strong>
                    {!sponsor.logoUrl && (
                      <div className="mt-1">
                        <SponsorLogoUpload
                          onUploadComplete={(logoUrl) => handleLogoUpload(index, logoUrl)}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveSponsor(index)}
                  className="px-3 py-1 text-xs border border-red-400 text-red-600 rounded-md hover:bg-red-50 transition-colors duration-200 cursor-pointer bg-transparent"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        <button
          type="submit"
          className="w-full mt-4 py-3 px-6 bg-secondary text-white text-sm font-semibold rounded-lg hover:bg-red-800 transition-colors duration-200 cursor-pointer border-none"
        >
          Update Livestream
        </button>
      </form>
    </AppModal>
  );
}

export default UpdateLivestreamModal;
